import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Dean = () => {
  const { t } = useTranslation();

  const scrollToContent = () => {
    document.getElementById('dean-content').scrollIntoView({
      behavior: 'smooth'
    });
  };

  return (
    <div className="font-sans">
      {/* Full-screen Video Hero */}
      <div className="h-screen w-full relative overflow-hidden">
        <video
          className="absolute top-1/2 left-1/2 w-full h-full object-cover transform -translate-x-1/2 -translate-y-1/2"
          src="/fenfakultesivideo.mp4"
          autoPlay
          loop
          muted
          playsInline
        />
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center">
          <motion.h1 
            className="text-5xl md:text-8xl font-black text-white uppercase" 
            style={{ textShadow: '0px 5px 20px rgba(0,0,0,0.5)' }}
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            {t('dean.title')}
          </motion.h1>
          <motion.p 
            className="mt-4 text-lg md:text-xl text-white/90 font-medium max-w-2xl" 
            style={{ textShadow: '0px 2px 10px rgba(0,0,0,0.5)' }}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            {t('dean.subtitle')}
          </motion.p>
        </div>
        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2 cursor-pointer z-20"
          onClick={scrollToContent}
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown className="w-10 h-10 text-white/80 hover:text-white transition-colors"/>
        </motion.div>
      </div>

      {/* Page Content */}
      <div id="dean-content" className="relative bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <div className="mb-12">
              <div className="relative w-full flex justify-center">
                <motion.img 
                  src="https://fs.hacettepe.edu.tr/fenyeni/fotogaleri/hatice%20mergen.jpg" 
                  alt="Prof. Dr. Hatice Mergen" 
                  className="rounded-lg shadow-2xl"
                  style={{ 
                    maxHeight: '800px',
                    width: 'auto',
                    maxWidth: '100%',
                    objectFit: 'contain'
                  }}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                />
              </div>
            </div>

            <div className="text-center mb-12">
              <motion.h2 
                className="text-3xl font-semibold text-hacettepe-blue mb-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                Prof. Dr. Hatice Mergen
              </motion.h2>
              <div className="space-y-4 text-gray-700">
                <motion.p 
                  className="text-xl"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  viewport={{ once: true }}
                >
                  {t('dean.description')}
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <p className="text-lg"><strong>{t('dean.email')}:</strong> mergen@hacettepe.edu.tr</p>
                </motion.div>
              </div>
            </div>

            <motion.div 
              className="bg-white p-8 rounded-lg border border-gray-200 mb-8 shadow-lg"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl font-semibold text-hacettepe-blue mb-4">{t('dean.message_title')}</h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  {t('dean.message.p1')}
                </p>
                <p>
                  {t('dean.message.p2')}
                </p>
                <p>
                  {t('dean.message.p3')}
                </p>
              </div>
            </motion.div>

            <motion.div
              className="bg-white p-8 rounded-lg border border-gray-200 shadow-lg"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl font-semibold text-hacettepe-blue mb-4">{t('dean.contact_info')}</h2>
              <div className="space-y-2 text-gray-700">
                <p><strong>{t('dean.email')}:</strong> mergen@hacettepe.edu.tr</p>
                <p><strong>{t('dean.phone')}:</strong> +90 (312) XXX XX XX</p>
                <p><strong>{t('dean.fax')}:</strong> +90 (312) XXX XX XX</p>
                <p><strong>{t('dean.address_label')}:</strong> {t('dean.address')}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dean; 